import React, { FC, useEffect, useState } from 'react';
import { Text, TextVariant } from '@naf/text';
import { StepWizardChildProps } from 'react-step-wizard';
import { Link } from '@naf-simba/elements-links';
import { Loader } from '@naf-simba/building-blocks-loaders';
import useSelector from '../../../../redux/typedHooks';
import { NAFGTMEvent } from '../../../../../../types/GTM/NAFGTMEvent';
import { useGTMDataLayer } from '../../../../hooks/GTM/useGTMDataLayer';
import { useTrackingStep4 } from '../../utils/useTracking';
import * as S from './Redirect.styles';
import { getStatusText } from './Redirect.utils';
import { MAIN_MEMBER_EMAIL_FIELD } from '../../lib/validateEmailRecognition';

export const Redirect: FC<Partial<StepWizardChildProps>> = ({ goToStep, currentStep }) => {
  const checkoutStep = 4;
  const [tempError, setTempError] = useState<string | undefined>(undefined);
  const isCreatingOrder = useSelector((state) => state.order.isCreatingOrder);
  const paymentOrderUrl = useSelector((state) => state.order.orderDetails?.paymentOrderUrl);
  const orderSummaries = useSelector((state) => state.order.orderDetails?.summary?.orderSummaries);
  const isCreated = useSelector((state) => state.order.isCreated);
  const serverError = useSelector((state) => state.order.validation?.validationResults);
  const campaignCode = useSelector((state) => state.becomeAMember?.data?.campaignCodeDetails?.campaignCode);

  const dataLayer = useGTMDataLayer();

  // If the error is unknown, i.e. null in validationDetails, display an error message for 3 seconds and return to step 2
  useEffect(() => {
    if (goToStep && dataLayer && currentStep) {
      if (serverError === null) {
        setTempError('Det oppstod en ukjent feil, prøv igjen om litt');
        setTimeout(() => {
          setTempError(undefined);
          dataLayer?.push({
            event: NAFGTMEvent.checkoutAction,
            checkout_step: checkoutStep,
            checkout_action: 'Ukjent valideringsfeil fra server',
            ...(campaignCode && { campaign_code: campaignCode }),
          });
          goToStep(Math.max((currentStep || 2) - 1, 1));
        }, 3000);
      }
      if (serverError) {
        dataLayer?.push({
          event: NAFGTMEvent.checkoutAction,
          checkout_step: checkoutStep,
          checkout_action: 'Kjent valideringsfeil fra server',
          ...(campaignCode && { campaign_code: campaignCode }),
        });
        const hasEmailRecognitionError = serverError.some((error) => error.field === MAIN_MEMBER_EMAIL_FIELD);
        goToStep(hasEmailRecognitionError ? 1 : Math.max(currentStep - 1, 1));
      }
    }
  }, [serverError, goToStep, dataLayer, currentStep, campaignCode]);

  const trackingStatus = useTrackingStep4(orderSummaries ? orderSummaries[0].productNumber : undefined);
  const statusText = getStatusText(isCreatingOrder, isCreated, tempError);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !!paymentOrderUrl &&
      (trackingStatus === 'done' || trackingStatus === 'error')
    ) {
      window.location.href = `${paymentOrderUrl}`;
    }
  }, [paymentOrderUrl, trackingStatus]);

  return (
    <>
      <S.LoaderWrapper>
        <Loader />
      </S.LoaderWrapper>
      {statusText && <S.AnimatedText variant={TextVariant.Header3}>{statusText}</S.AnimatedText>}
      {tempError && <S.AnimatedText variant={TextVariant.Header3}>{tempError}</S.AnimatedText>}
      <S.LoaderContainer>
        {isCreated && (
          <>
            <Text variant={TextVariant.Header3}>Blir den stående og spinne?</Text>
            <Link href={paymentOrderUrl} text="Klikk og prøv en gang til" />
          </>
        )}
      </S.LoaderContainer>
    </>
  );
};
